/**
 * refsSlice — per-org reference data for the React Calendar Month View.
 *
 * Holds the six list resources the page needs but that are NOT blocking
 * the first render: resources, locations, custom fields, pilots, mechanics,
 * activity types. The page may render with `status === 'loading'` so children
 * show partial data as it arrives, but the slice owns the contract that
 * nothing here comes from `window.serverData`.
 *
 * `fetchRefs(orgUnique)` runs all six Symfony list endpoints in parallel
 * via `Promise.allSettled`. On success the slice flips to `succeeded` and the
 * page can populate filter pickers.
 *
 * @module refsSlice
 */

import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import CalendarApi from '../../api/CalendarApi.js';
import { listCustomFields, listMechanics, listActivityTypes } from '../../api/orgResourcesApi.js';
import { unwrapList } from './utils/apiEnvelope.js';
import {
	normalizeFilteredPilot,
	normalizeFilteredInstructor,
	normalizeFilteredResource,
	normalizeCalendarLocation
} from './utils/normalizeCalendarRefs.js';

// Re-export so existing test imports keep working — the helper now lives in
// `utils/apiEnvelope.js` (Phase 1, Remediation 2, Task #1) but
// `refsSlice.test.js` imports `unwrapList` from this module directly.
export { unwrapList };

/**
 * @typedef {'idle'|'loading'|'succeeded'|'failed'} FetchStatus
 *
 * @typedef {Object} ActivityType
 * @property {string} id
 * @property {string} name
 * @property {string} color
 * @property {boolean} trash
 *
 * @typedef {Object} RefsState
 * @property {Array<Object>} resources
 * @property {Array<Object>} locations
 * @property {Array<Object>} customFields
 * @property {Array<Object>} pilots
 * @property {Array<Object>} mechanics
 * @property {Object<string, ActivityType>} activityTypes  Map keyed by activity-type ID
 * @property {FetchStatus} status
 * @property {string|null} error
 * @property {string|null} orgUnique
 */

/** @type {RefsState} */
export const initialState = {
	resources: [],
	locations: [],
	customFields: [],
	pilots: [],
	mechanics: [],
	// Activity-types color map keyed by ID — `{ [id]: { id, name, color, trash } }`.
	// Consumed by the month-view "Activity Type Color Scheme" chip coloring.
	activityTypes: {},
	status: 'idle',
	error: null,
	orgUnique: null
};

/**
 * Tolerant trashed-flag check. Symfony may serialize the entity `trash`
 * field as a boolean, while legacy payloads use the string `'1'` / `'0'`.
 * Accept every truthy-trash representation.
 *
 * @param {*} v
 * @returns {boolean}
 */
function isTrashed(v) {
	return v === true || v === 1 || v === '1';
}

/**
 * Normalize the activity-types list endpoint payload into a map keyed by ID:
 * `{ [id]: { id, name, color, trash } }`. Mirrors the day-view `activityTypes`
 * map shape (`calendar-day.js:83-114`). `trash` is normalized to a boolean.
 *
 * @param {Array<Object>} list
 * @returns {Object<string, ActivityType>}
 */
export function indexActivityTypes(list) {
	const map = {};
	(list || []).forEach(at => {
		if (!at || at.id == null) return;
		const id = String(at.id);
		map[id] = {
			id,
			name: at.name ?? '',
			color: at.color ?? '',
			trash: isTrashed(at.trash)
		};
	});
	return map;
}

/**
 * fetchRefs — parallel fetch of all 6 reference list endpoints.
 *
 * @param {string} orgUnique
 */
export const fetchRefs = createAsyncThunk(
	'calendarMonthRefs/fetch',
	async (arg, { rejectWithValue }) => {
		// Accepts the legacy string form (month) or `{ orgUnique, skipMechanics }`
		// (day view defers the slow /org/{org}/mechanics call — modal-only data —
		// off the first-paint critical path; see fetchMechanics below).
		const orgUnique = typeof arg === 'string' ? arg : arg?.orgUnique;
		const skipMechanics = typeof arg === 'object' && !!arg?.skipMechanics;
		if (!orgUnique) {
			return rejectWithValue('Missing organization slug');
		}
		// Refs are sourced from the global /api/v1/calendar/* endpoints so
		// multi-org filter dropdowns see pilots/resources/locations from
		// every org the user has VIEW_CALENDAR permission in. The legacy
		// org-scoped `/org/{org}/pilots` only returned a single org and
		// broke the multi-org filtering the product spec requires.
		// Custom fields + mechanics stay on the per-org endpoints —
		// custom fields are truly org-scoped, and mechanics feed the
		// discrepancy assign-to picker (org-local by design).
		//
		// `Promise.allSettled` instead of `Promise.all`: any one rejected
		// fetch must NOT blank the other dropdowns. Real-world case
		// (Rem16 Bug U): a user without `/custom-fields` permission
		// receives a 403 and `Promise.all` fast-fails — all 5 other
		// successful results get discarded and the filter UI renders
		// empty. With `allSettled` each list is handled independently;
		// `unwrapList(null)` returns `[]` so rejected fetches yield
		// empty arrays without leaking `null`/`undefined` into reducer
		// state.
		// `listActivityTypes()` (index 5) is org-scoped via `buildOrgEndpoint`
		// → resolves to the current dashboard org (calendar is locked to one
		// org per Rem30 Task 3). It joins the same `allSettled` batch so a
		// failed activity-types fetch never blanks the other dropdowns.
		//
		// Custom fields are intentionally NOT fetched here: the standalone
		// `fetchCustomFields` thunk (dispatched alongside `fetchRefs` from
		// `CalendarMonthV2Page`) already issues the single `/org/{org}/custom-fields`
		// request and populates both the slice and the bridge. Fetching it again
		// in this batch duplicated the network call on every calendar load.
		const results = await Promise.allSettled([
			CalendarApi.listResources(), // 0
			CalendarApi.listLocations(), // 1
			CalendarApi.listPilots(), // 2
			CalendarApi.listInstructors(), // 3
			skipMechanics ? Promise.resolve(null) : listMechanics(), // 4
			listActivityTypes() // 5
		]);

		// Surface every failure into the console so 403s / 500s are still
		// debuggable. Non-blocking by design — the slice continues with
		// whatever data did arrive.
		results.forEach((r, idx) => {
			if (r.status === 'rejected') {
				const label = [
					'resources',
					'locations',
					'pilots',
					'instructors',
					'mechanics',
					'activityTypes'
				][idx];
				// eslint-disable-next-line no-console
				console.warn(`[calendarMonthRefs] ${label} fetch failed:`, r.reason);
			}
		});

		// Hard-fail only when every fetch rejected — that's an auth /
		// network blowout, not a permissions edge case. Bubble the first
		// reason up to the rejected reducer so the page can show an error
		// state instead of a silently empty filter panel.
		const consideredForHardFail = skipMechanics ? results.filter((_, i) => i !== 4) : results;
		if (consideredForHardFail.every(r => r.status === 'rejected')) {
			const firstErr = results[0].reason;
			const message =
				firstErr?.response?.data?.error?.[0] ||
				firstErr?.message ||
				'Failed to load reference data';
			return rejectWithValue(message);
		}

		// `unwrapList` throws on `{success:false}` envelopes from otherwise-200
		// fulfilled responses. Treat that the same as a network-level rejection
		// (per-list empty array) so one bad envelope doesn't blank the other
		// dropdowns. The console.warn above already logged the underlying error.
		const safeUnwrap = (i, key) => {
			if (results[i].status !== 'fulfilled') return [];
			try {
				return unwrapList(results[i].value, key);
			} catch (e) {
				const label = [
					'resources',
					'locations',
					'pilots',
					'instructors',
					'mechanics',
					'activityTypes'
				][i];
				// eslint-disable-next-line no-console
				console.warn(`[calendarMonthRefs] ${label} envelope error:`, e);
				return [];
			}
		};
		const rawResources = safeUnwrap(0, 'resources');
		const rawInstructors = safeUnwrap(3, 'instructors');
		return {
			// Merge aircraft/sim resources and instructors into a single list
			// so the existing `resourcesMapBridge` + `instructorsMapBridge`
			// (which filters by `type==="instructor"`) can continue to split
			// them downstream.
			resources: [
				...rawResources.map(normalizeFilteredResource),
				...rawInstructors.map(normalizeFilteredInstructor)
			],
			// `normalizeCalendarLocation` flattens the nested
			// `location.organization.{id,unique,name}` onto the root so
			// `cascadeLocations` + `buildGroups` work without extra joins.
			locations: safeUnwrap(1).map(normalizeCalendarLocation),
			// NOTE: customFields are populated by the standalone `fetchCustomFields`
			// thunk (see above) — intentionally not returned here.
			pilots: safeUnwrap(2, 'pilots').map(normalizeFilteredPilot),
			// `null` (not []) when skipped — reducers/bridges keep prior data.
			mechanics: skipMechanics ? null : safeUnwrap(4),
			// Activity-types list → map keyed by ID for chip coloring (Task 6).
			activityTypes: indexActivityTypes(safeUnwrap(5, 'activityTypes'))
		};
	}
);

/**
 * fetchCustomFields — independent fetcher that bypasses the bulk `fetchRefs`
 * `Promise.all`. Custom fields are tiny but were arriving ~20s late because
 * they were bundled with the slow `/calendar/resources` endpoint. Running it
 * standalone lets the CreateEventModal render its per-org fields almost
 * immediately on bootstrap.
 *
 * The bridge (`customFieldsMapBridge` in `bridgeReducers.js`) listens to both
 * this thunk's fulfilled action AND `REFS_OK`, so the bulk path still works
 * for first-load ordering and for code paths that pre-date this split.
 */
export const fetchCustomFields = createAsyncThunk(
	'calendarMonthRefs/fetchCustomFields',
	async (orgUnique, { rejectWithValue }) => {
		if (!orgUnique) return rejectWithValue('Missing organization slug');
		try {
			const res = await listCustomFields();
			return { customFields: unwrapList(res) };
		} catch (err) {
			const message =
				err?.response?.data?.error?.[0] || err?.message || 'Failed to load custom fields';
			return rejectWithValue(message);
		}
	}
);

/**
 * fetchMechanics — standalone fetcher for the (slow) org-scoped mechanics
 * list. Mechanics only feed the discrepancy assign-to picker inside the
 * event modal, so the Day view defers this until the first modal opens
 * instead of paying ~3s on the first-paint critical path.
 */
export const fetchMechanics = createAsyncThunk(
	'calendarMonthRefs/fetchMechanics',
	async (orgUnique, { rejectWithValue }) => {
		if (!orgUnique) return rejectWithValue('Missing organization slug');
		try {
			const res = await listMechanics();
			return { mechanics: unwrapList(res) };
		} catch (err) {
			const message =
				err?.response?.data?.error?.[0] || err?.message || 'Failed to load mechanics';
			return rejectWithValue(message);
		}
	}
);

const refsSlice = createSlice({
	name: 'calendarMonthRefs',
	initialState,
	reducers: {
		resetRefs() {
			return initialState;
		}
	},
	extraReducers: builder => {
		builder
			.addCase(fetchRefs.pending, (state, action) => {
				state.status = 'loading';
				state.error = null;
				state.orgUnique = action.meta.arg || null;
			})
			.addCase(fetchRefs.fulfilled, (state, action) => {
				state.status = 'succeeded';
				state.error = null;
				state.resources = action.payload.resources;
				state.locations = action.payload.locations;
				// customFields intentionally NOT set here — owned by the standalone
				// `fetchCustomFields` thunk (network-deduped). Setting it from this
				// (now customFields-less) payload would clobber it with `undefined`.
				state.pilots = action.payload.pilots;
				// null = mechanics skipped this pass (day view lazy path) — keep
				// whatever fetchMechanics already loaded.
				if (action.payload.mechanics !== null) state.mechanics = action.payload.mechanics;
				state.activityTypes = action.payload.activityTypes;
			})
			.addCase(fetchRefs.rejected, (state, action) => {
				state.status = 'failed';
				state.error = action.payload || action.error?.message || 'Refs fetch failed';
			})
			.addCase(fetchCustomFields.fulfilled, (state, action) => {
				// Standalone path — overwrite the slice's customFields ASAP
				// without touching the bulk `status` flag (other refs may
				// still be loading).
				state.customFields = action.payload.customFields;
			})
			.addCase(fetchMechanics.fulfilled, (state, action) => {
				state.mechanics = action.payload.mechanics || [];
			});
	}
});

export const { resetRefs } = refsSlice.actions;

// ────────────────────────────────────────────────────────────────────────────
// Selectors — activity-types color map (consumed by Rem30 Task 6)
// ────────────────────────────────────────────────────────────────────────────

// Stable empty-map reference so `selectActivityTypes` returns the same object
// when the slice is still empty — keeps `createSelector` memoization intact.
const EMPTY_ACTIVITY_TYPES = Object.freeze({});

/**
 * Raw activity-types map keyed by ID: `{ [id]: { id, name, color, trash } }`.
 * Includes trashed types — most callers want `selectActiveActivityTypes`.
 *
 * @param {Object} state Redux root state
 * @returns {Object<string, ActivityType>}
 */
export const selectActivityTypes = state =>
	state?.calendarMonthRefs?.activityTypes || EMPTY_ACTIVITY_TYPES;

/**
 * Active (non-trashed) activity-types map keyed by ID. This is the map the
 * month-view "Activity Type Color Scheme" chip coloring (Task 6) consumes.
 * Memoized — recomputes only when the underlying activity-types map changes.
 *
 * @returns {Object<string, ActivityType>}
 */
export const selectActiveActivityTypes = createSelector([selectActivityTypes], all => {
	const active = {};
	Object.keys(all).forEach(id => {
		const at = all[id];
		if (at && !at.trash) active[id] = at;
	});
	return active;
});

export default refsSlice.reducer;
